<script>
  // @ts-check
  import { afterUpdate, createEventDispatcher, onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";

  import Logo from "../../atoms/Logo.svelte";
  import Icon from "../../atoms/Icon.svelte";
  import { getTheme } from "../../utilities/themes";

  import Constants from "./globalNavigationConstants";

  /**
   * Additional classes which apply to the container
   * @type { string }
   * */
  let className = "";
  export { className as class };

  /** @type {HTMLElement | undefined} */
  let backButtonRef;

  /** @type {HTMLElement | undefined} */
  let logoButtonRef;

  /** Primarily used for testing @type {string} */
  export let id = undefined;

  /** @type {import('../../utilities/themes.js').ThemeName} */
  export let theme = "white";

  /** @type {string} */
  export let mainLogoAccessibilityText;

  /** @type {import('../../utilities/themes').Theme}*/
  let currentTheme;

  /** @type {string} */
  export let displayedMobileSection;

  /** @type {boolean} */
  export let isSearchPanelVisible;

  /** @type {boolean} */
  export let hasScrolledPastNav;

  /** @type {boolean} */
  export let delayThemeFadeOnNavBar;

  let hasMounted = false;
  let hamburgerClasses;

  /** @type {boolean} */
  export let isExpanded;

  const dispatch = createEventDispatcher();

  $: currentTheme = getTheme(theme);
  $: componentColorTheme = currentTheme.componentColorTheme;
  $: isOpen = isSearchPanelVisible || isExpanded;

  onMount(() => {
    hamburgerClasses = hamburgerButtonThemeClassesTable[componentColorTheme];
    hasMounted = true;
  });

  $: {
    if (componentColorTheme && hasMounted) {
      setTimeout(
        () => {
          hamburgerClasses = hamburgerButtonThemeClassesTable[componentColorTheme];
        },
        isOpen || delayThemeFadeOnNavBar ? 0 : 650
      );
    }
  }

  const handleHamburgerClick = () => {
    dispatch("hamburgerClick");
  };

  const handleSearchClose = () => {
    dispatch("searchClose");
  };

  const hamburgerButtonThemeClassesTable = {
    white: "after:bg-black before:bg-black",
    lightGrey: "after:bg-black before:bg-black",
    black: "after:bg-white before:bg-white"
  };

  const buttonClassesWhenMenuOpened = "before:rotate-[45deg] after:rotate-[-45deg]";
  const buttonClassesWhenMenuClosed = "after:translate-y-[6px] before:-translate-y-[6px]";

  /** @type {boolean} */
  let shouldFocusLogoButton = false;

  const handleGoBack = () => {
    const hasFocus = backButtonRef === document.activeElement;
    if (hasFocus) {
      shouldFocusLogoButton = true;
    }
    dispatch("goBack");
  };

  $: backArrowVisibilityClasses = isExpanded ? "" : "hidden";
  $: transitionClass = "transition-all duration-[300ms] ease-nav-easing";
  $: transitionDelayClass =
    isOpen || delayThemeFadeOnNavBar || hasScrolledPastNav ? "" : "delay-[650ms]";

  $: logoClass = `absolute bottom-1/2 left-0 top-1/2 h-full w-full -translate-y-1/2 transition-all duration-[150ms] ease-nav-easing ${transitionDelayClass}`;
  $: logoOpacityClass = componentColorTheme === "white" ? "opacity-[1]" : "opacity-[0]";
  $: logoDarkOpacityClass = componentColorTheme === "white" ? "opacity-[0]" : "opacity-[1]";

  const menuButtonClasses = `before:absolute before:left-0 before:transition-all before:duration-300 before:w-full before:h-[2px] before:block before:ease-nav-easing after:absolute after:left-0 after:transition-all after:duration-300 after:w-full after:h-[2px] after:block after:ease-nav-easing`;

  afterUpdate(() => {
    if (shouldFocusLogoButton && displayedMobileSection === undefined) {
      shouldFocusLogoButton = false;
      logoButtonRef?.focus();
    }
  });
</script>

<div
  {id}
  class="override-nav relative z-[1] flex h-nav-height-mobile w-full items-center justify-between {currentTheme.textColor} {transitionClass} {transitionDelayClass} {className}"
>
  <div class="relative">
    {#if displayedMobileSection !== undefined}
      <button
        bind:this={backButtonRef}
        class:hidden={!isExpanded}
        aria-label="Go back to previous navigation menu"
        on:click={handleGoBack}
        class="absolute bottom-0 top-0 m-auto grid place-items-center {currentTheme.focusOutlineColor}  {currentTheme.focusTextColor} h-[22px] w-[22px] {backArrowVisibilityClasses} focus-visible:outline-dashed focus-visible:outline-offset-2"
        in:fly={{ duration: 300, easing: cubicInOut }}
        out:fly={{ duration: 300, easing: cubicInOut }}
      >
        <Icon name="arrowLeft" inheritSize />
      </button>
    {:else}
      <a
        data-sveltekit-reload
        href="/"
        data-transition-focus={Constants.transitionFocus.logo}
        bind:this={logoButtonRef}
        class="{currentTheme.focusOutlineColor} relative grid h-[44px] w-[44px] place-items-center focus-visible:outline-dashed focus-visible:outline-offset-2"
        in:fly={{ duration: 300, easing: cubicInOut }}
        out:fly={{ duration: 300, easing: cubicInOut }}
      >
        <span class="sr-only">{mainLogoAccessibilityText}</span>
        <Logo type="obamaFoundationIcon" class="{logoClass} {logoOpacityClass}" />
        <Logo type="obamaFoundationIconDarkMode" class="{logoClass} {logoDarkOpacityClass}" />
      </a>
    {/if}
  </div>
  <div class="flex items-center gap-6">
    {#if !displayedMobileSection && !isSearchPanelVisible}
      <div
        class="flex items-center gap-6"
        in:fade={{ duration: 150, delay: 650, easing: cubicInOut }}
        out:fade={{ duration: 150, easing: cubicInOut }}
      >
        <slot name="utilityNavigation" />
      </div>
    {/if}
    <button
      on:click={isSearchPanelVisible ? handleSearchClose : handleHamburgerClick}
      aria-label="{isExpanded || isSearchPanelVisible
        ? 'Close'
        : 'Open'} Mobile {isSearchPanelVisible ? 'Search Panel' : 'Menu'}"
      aria-expanded={isExpanded || isSearchPanelVisible}
      class="
        relative flex h-[24px] w-[24px] items-center justify-center focus-visible:outline-dashed focus-visible:outline-offset-2
        {hamburgerClasses} {menuButtonClasses}
        {currentTheme.focusOutlineColor} {currentTheme.focusTextColor}
        {isExpanded || isSearchPanelVisible
        ? buttonClassesWhenMenuOpened
        : buttonClassesWhenMenuClosed}"
    >
      <span
        class="block h-[2px] w-[24px] before:absolute before:left-0 before:block before:h-[2px] before:w-full before:transition-all before:duration-300 before:ease-nav-easing
        {hamburgerClasses}
        {isExpanded || isSearchPanelVisible ? 'opacity-[0]' : 'opacity-[1]'}"
      ></span>
    </button>
  </div>
</div>
